"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useTransition } from "react";

import { Select } from "~/components/ui/select";
import { setSortParam } from "~/lib/storefront/sort-params";
import type { ListingSort } from "~/server/api/schemas/listing";
import { useTranslator } from "~/i18n/provider";

/**
 * P2-12 · Sort control. A native `<select>` (a bottom sheet on mobile, fully
 * keyboard-operable and labelled for free) that writes `sort=` to the URL —
 * dropping the default for canonical links — and refetches via App-Router
 * navigation, preserving filters and resetting to page 1.
 */
export function SortDropdown({
  sort,
  defaultSort,
  options,
}: {
  sort: ListingSort;
  defaultSort: ListingSort;
  options: { key: ListingSort; label: string }[];
}) {
  const t = useTranslator();
  const router = useRouter();
  const pathname = usePathname();
  const params = useSearchParams();
  const [isPending, startTransition] = useTransition();

  const onChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const key = event.target.value as ListingSort;
    const next = setSortParam(params.toString(), key, defaultSort);
    startTransition(() =>
      router.push(next ? `${pathname}?${next}` : pathname, { scroll: false }),
    );
  };

  return (
    <label className="flex items-center gap-2 text-sm text-fg-muted">
      <span className="hidden sm:inline">{t("browse.sort")}</span>
      <Select
        aria-label={t("browse.sortProducts")}
        value={sort}
        onChange={onChange}
        disabled={isPending}
        className="h-9 w-auto pr-8"
      >
        {options.map((o) => (
          <option key={o.key} value={o.key}>
            {o.label}
          </option>
        ))}
      </Select>
    </label>
  );
}
